import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  
  const isHomePage = location.pathname === "/";

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: "/" },
    { 
      name: t('nav.services'), 
      path: "/services",
      dropdownId: "services",
      subtitle: t('nav.ourExpertise'),
      dropdown: [
        { name: t('nav.allServices'), path: "/services", highlight: true },
        { name: t('nav.visibility'), path: "/services/visibilite" },
        { name: t('nav.brandImage'), path: "/services/image-de-marque" },
        { name: t('nav.growth'), path: "/services/croissance" },
      ]
    },
    { 
      name: t('nav.offers'), 
      path: "/offres",
      dropdownId: "offres",
      subtitle: t('nav.ourFormulas'),
      dropdown: [
        { name: t('nav.allOffers'), path: "/offres", highlight: true },
        { name: t('nav.packVisibility'), path: "/offres/visibilite-360" },
        { name: t('nav.packImage'), path: "/offres/image-premium" },
        { name: t('nav.packGrowth'), path: "/offres/croissance-plus" },
        { name: t('nav.servicesCart'), path: "/offres/services-carte" },
        { name: t('nav.support360'), path: "/offres/accompagnement-360" },
      ]
    },
    { name: t('nav.about'), path: "/a-propos" },
    { name: t('nav.contact'), path: "/contact" },
  ];

  const handleMouseEnter = (dropdownId) => {
    setActiveDropdown(dropdownId);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  // Determine navbar style based on page and scroll position
  const isTransparent = isHomePage && !isScrolled;

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isTransparent 
        ? 'bg-transparent' 
        : 'bg-white shadow-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className={`text-2xl font-bold transition-colors duration-300 ${
              isTransparent ? 'text-white' : 'text-gray-900'
            }`}>
              ImpaKt <span className="text-cyan-500">Studio</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.dropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(link.dropdownId)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`px-4 py-2 flex items-center gap-1 font-medium transition-colors duration-300 ${
                        isTransparent 
                          ? 'text-white/90 hover:text-white' 
                          : 'text-gray-700 hover:text-cyan-600'
                      }`}
                      data-testid={`${link.dropdownId}-dropdown`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === link.dropdownId ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === link.dropdownId && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                        <p className="px-4 py-2 text-xs text-gray-500 font-medium uppercase tracking-wider">
                          {link.subtitle}
                        </p>
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className={`block px-4 py-2.5 transition-colors ${
                              item.highlight 
                                ? 'text-cyan-600 font-semibold bg-cyan-50 hover:bg-cyan-100' 
                                : 'text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
                            }`}
                            data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-4 py-2 font-medium transition-colors duration-300 ${
                      isTransparent 
                        ? 'text-white/90 hover:text-white' 
                        : 'text-gray-700 hover:text-cyan-600'
                    } ${location.pathname === link.path ? 'text-cyan-500' : ''}`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-300 ml-2 ${
                isTransparent 
                  ? 'border-white/30 text-white hover:bg-white/10' 
                  : 'border-gray-300 text-gray-700 hover:border-cyan-500 hover:text-cyan-600'
              }`}
              aria-label={language === 'fr' ? 'Switch to English' : 'Passer en Français'}
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium text-sm">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            <Link
              to="/contact"
              className="ml-4 px-6 py-2.5 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg"
              data-testid="cta-parlons-en"
            >
              {t('nav.letsTalk')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-all duration-300 ${
                isTransparent 
                  ? 'border-white/30 text-white' 
                  : 'border-gray-300 text-gray-700'
              }`}
              data-testid="mobile-language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="text-xs font-medium">{language.toUpperCase()}</span>
            </button>
            
            <button
              className={`p-2 transition-colors duration-300 ${
                isTransparent ? 'text-white' : 'text-gray-900'
              }`}
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-white rounded-b-2xl shadow-xl pb-6 absolute top-20 left-0 right-0 mx-4">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.path}
                  className="block px-4 py-3 text-gray-700 hover:bg-cyan-50 hover:text-cyan-600 font-medium"
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
                {link.dropdown && link.dropdown.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block px-8 py-2 text-sm ${item.highlight ? 'text-cyan-600 font-medium' : 'text-gray-500 hover:text-cyan-600'}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            ))}
            <div className="px-4 mt-4">
              <Link
                to="/contact"
                className="block text-center px-6 py-3 bg-cyan-500 text-white font-semibold rounded-full"
                onClick={() => setIsOpen(false)}
              >
                {t('nav.letsTalk')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
